// Import the SVG file
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
} from '@langchain/untitled-ui-icons';

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useSegmentAnalyticsContext } from '@/SegmentAnalyticsProvider';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { Logo } from '@/components/Logo/Logo';
import { VideoBackgroundPageWrapper } from '@/components/VideoBackgroundPageWrapper';
import { Spinner, Text } from '@/design-system';
import { cn } from '@/design-system/utils/cn';
import { useColorScheme } from '@/hooks/useColorScheme';
import { useTenantList } from '@/hooks/useSwr';
import { appOrganizationPath } from '@/utils/constants';

import { OnboardingOptions } from './components/OnboardingOptions';
import { SetupEvaluation } from './components/SetupEvaluation';
import { SetupObservability } from './components/SetupObservability';
import {
  OnboardingStepV2,
  Projects,
  Roles,
  projectToText,
  roleToText,
} from './constants';
import { useCreateOnboardingStateOnLoad } from './hooks/useCreateOnboardingStateOnLoad';
import { useCreateOrgOnLoad } from './hooks/useCreateOrgOnLoad';
import { useOnboardingStep } from './hooks/useOnboardingStep';
import { useRedirectExistingUser } from './hooks/useRedirectExistingUser';

export const OnboardingV2 = () => {
  const { analytics } = useSegmentAnalyticsContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [waitingForOrgCreation, setWaitingForOrgCreation] = useState(false);
  // create the user org in the background when the user lands on the onboarding page
  const { createOrgError, hasCreatedOrg } = useCreateOrgOnLoad({
    waitingForOrgCreation,
    setWaitingForOrgCreation,
    setSearchParams,
  });
  useCreateOnboardingStateOnLoad();

  const navigate = useNavigate();
  const { step, setStep } = useOnboardingStep({
    searchParams,
    setSearchParams,
  });
  const [selectedRole, setSelectedRole] = useState<string>();
  const [otherRole, setOtherRole] = useState<string>();
  const [selectedProject, setSelectedProject] = useState<string>();

  const { data: tenantList, isLoading: isLoadingTenantList } = useTenantList();
  const newTenant = tenantList?.[0];
  const newTenantId = newTenant?.id;

  // if the first time  we load the tenants list there is a tenant, that means the user has already created an org
  // and we need to do the redirect
  const [firstTenantId, setFirstTenantId] = useState<string | null | undefined>(
    undefined
  );

  // when the tenant list loads, if we haven't set the first tenant id, set it to the first tenant id
  // or if no tenant is found, set it to null. This explicit difference between undefined and null lets us know
  // if we still need to set firstTenantId or not
  useEffect(() => {
    if (tenantList && firstTenantId === undefined) {
      setFirstTenantId(tenantList[0]?.id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to run this effect when the tenant list loads
  }, [tenantList]);

  useRedirectExistingUser({ skipRedirect: !firstTenantId, newTenantId, step });

  const skip = useCallback(() => {
    if (newTenantId) {
      navigate(`/${appOrganizationPath}/${newTenantId}`);
    } else {
      navigate('/');
    }
  }, [newTenantId, navigate]);
  const advanceStep = () => setStep(step + 1);
  const goBack = () => setStep(step - 1);

  const { isDarkMode } = useColorScheme();

  const onSubmitQuestionnaire = (
    role: string | undefined,
    project: string | undefined
  ) => {
    if (!role && !project) {
      analytics?.track('Onboarding Survey Skipped');
      return;
    }
    analytics?.track('Onboarding Survey Answered', {
      role: role === Roles.OTHER ? otherRole : role,
      project,
    });
  };

  if (isLoadingTenantList) return null;

  if (step === OnboardingStepV2.Options) {
    return (
      <VideoBackgroundPageWrapper>
        <div
          className="flex h-full w-full bg-primary"
          style={{
            transitionDuration: '700ms',
            transformOrigin: 'right',
            transform: 'scaleX(2)',
          }}
        />
        <div className="absolute left-0 top-0">
          <OnboardingOptions onSkip={skip} newTenantId={newTenantId} />
        </div>
      </VideoBackgroundPageWrapper>
    );
  }

  if (step === OnboardingStepV2.SetupObservability) {
    return (
      <SetupObservability
        onNext={skip}
        className={'duration-300 ease-in-out animate-in fade-in'}
      />
    );
  }
  if (step === OnboardingStepV2.SetupEvaluation) {
    return (
      <SetupEvaluation
        className="duration-300 ease-in-out animate-in fade-in"
        onNext={skip}
      />
    );
  }

  return (
    <VideoBackgroundPageWrapper>
      <div
        className={cn(
          'z-10',
          'flex h-full w-[50%] min-w-[776px] flex-col items-start justify-start rounded-r-[83px] bg-primary p-[72px]',
          'shadow-[0_675px_189px_0_rgba(138,118,158,0.00),0_432px_173px_0_rgba(138,118,158,0.01),0_243px_146px_0_rgba(138,118,158,0.05),0_108px_108px_0_rgba(138,118,158,0.09),0_27px_59px_0_rgba(138,118,158,0.10)]'
        )}
      >
        <Logo
          size="lg"
          className={cn(
            '-ml-8 h-[27px] w-[250px] shrink-0',
            isDarkMode ? 'text-[var(--purple-950)]' : 'text-brand-primary'
          )}
        />
        {step === OnboardingStepV2.Role && (
          <RoleStep
            advanceStep={advanceStep}
            selectedRole={selectedRole}
            setSelectedRole={setSelectedRole}
            otherRole={otherRole}
            setOtherRole={setOtherRole}
          />
        )}
        {step === OnboardingStepV2.Project && (
          <ProjectStep
            selectedProject={selectedProject}
            setSelectedProject={setSelectedProject}
            selectedRole={selectedRole}
            onBack={goBack}
            onSubmit={(role, project) => {
              onSubmitQuestionnaire(role, project);
              if (!hasCreatedOrg) {
                setWaitingForOrgCreation(true);
                return;
              }
              advanceStep();
            }}
          />
        )}
        {createOrgError && (
          <div className="my-4 bg-background">
            <ExpandableErrorAlert
              error={`Error creating organization. Reload the page to try again. If the problem persists, please contact support at support@langchain.dev. ${createOrgError}`}
            />
          </div>
        )}
        <div className="mt-auto flex items-center gap-4">
          <button
            type="button"
            className="flex items-center gap-2 border-b border-[var(--brand-primary)] text-brand-primary disabled:opacity-50"
            disabled={waitingForOrgCreation}
            onClick={() => {
              if (step === OnboardingStepV2.Project) {
                onSubmitQuestionnaire(selectedRole, selectedProject);
                if (!hasCreatedOrg) {
                  setWaitingForOrgCreation(true);
                  return;
                }
              }
              advanceStep();
            }}
          >
            Skip
            {waitingForOrgCreation ? (
              <Spinner />
            ) : (
              <ArrowRightIcon className="size-4" />
            )}
          </button>
        </div>
      </div>
    </VideoBackgroundPageWrapper>
  );
};

const RoleStep = ({
  advanceStep,
  selectedRole,
  setSelectedRole,
  otherRole,
  setOtherRole,
}: {
  advanceStep: () => void;
  selectedRole: string | undefined;
  setSelectedRole: (role: string) => void;
  otherRole: string | undefined;
  setOtherRole: (role: string) => void;
}) => {
  return (
    <>
      <Text
        variant="h1"
        weight="normal"
        className="mt-[125px] text-start text-5xl leading-[1.2] tracking-[-0.06em]"
      >
        What’s your primary role?
      </Text>
      <div className="mt-12 flex flex-col items-start justify-start gap-[18px] overflow-y-auto bg-transparent">
        {Object.values(Roles).map((role) => (
          <div className="flex items-start gap-2">
            <button
              key={role}
              type="button"
              className={cn(
                'h-[64px] rounded-full border border-brand-strong px-[34px] py-[10px] hover:bg-brand-green-50 dark:border-primary',
                selectedRole === role && 'bg-brand-green-50'
              )}
              onClick={() => {
                setSelectedRole(role);
                if (role !== Roles.OTHER) {
                  advanceStep();
                }
              }}
            >
              <Text variant="h2" weight="normal">
                {roleToText[role]}
              </Text>
            </button>
            {selectedRole === Roles.OTHER && role === Roles.OTHER && (
              <div className="flex items-end gap-2 ">
                <input
                  type="text"
                  className={cn(
                    'h-[64px] rounded-full border border-brand-strong bg-transparent px-5 py-2',
                    'focus:border-brand-strong'
                  )}
                  value={otherRole}
                  onChange={(e) => setOtherRole(e.target.value)}
                  autoFocus
                />
                <button
                  type="button"
                  className={cn(
                    'h-[64px] w-[64px] rounded-full px-5 py-2 hover:bg-brand-green-50 disabled:opacity-50 disabled:hover:bg-[#1C3C3C]',
                    'bg-[#1C3C3C] text-white hover:bg-[#1C3C3C]/90 active:bg-[#1C3C3C]/70'
                  )}
                  onClick={advanceStep}
                  disabled={!otherRole}
                >
                  <CheckIcon className="size-6" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

const ProjectStep = ({
  selectedProject,
  setSelectedProject,
  selectedRole,
  onBack,
  onSubmit,
}: {
  selectedProject: string | undefined;
  setSelectedProject: (project: string) => void;
  selectedRole: string | undefined;
  onBack: () => void;
  onSubmit: (role: string | undefined, project: string | undefined) => void;
}) => {
  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="-ml-2 mt-[50px] flex items-center gap-2 rounded-md p-2 hover:bg-secondary"
      >
        <ArrowLeftIcon className="size-8" />
      </button>
      <Text
        variant="h1"
        weight="normal"
        className="mt-[27px] text-start text-5xl leading-[1.2] tracking-[-0.06em]"
      >
        What stage is your current
        <Text
          variant="h1"
          weight="normal"
          className="text-start text-5xl leading-[1.2] tracking-[-0.06em]"
        >
          AI project?
        </Text>
      </Text>
      <div className="mt-12 flex h-full flex-col items-start justify-start gap-[18px] overflow-y-auto bg-transparent">
        {Object.values(Projects).map((project) => (
          <button
            key={project}
            type="button"
            className={cn(
              'h-[64px] rounded-full border border-brand-strong px-[34px] py-[10px] hover:bg-brand-green-50 dark:border-primary',
              selectedProject === project && 'bg-brand-green-50'
            )}
            onClick={() => {
              setSelectedProject(project);
              onSubmit(selectedRole, project);
            }}
          >
            <Text variant="h2" weight="normal">
              {projectToText[project]}
            </Text>
          </button>
        ))}
      </div>
    </>
  );
};
